import { useState, useRef, useCallback, type DragEvent } from 'react';

/**
 * Tiny HTML5-drag list reorder hook. Caller owns the data and the persistence.
 *
 * Usage:
 *   const drag = useDragSort(items.length, async (from, to) => {
 *     // reorder client-side, then persist via API (e.g. PUT each item's sort_order)
 *   });
 *   <li
 *     draggable
 *     onDragStart={drag.handlers(i).onDragStart}
 *     onDragOver={drag.handlers(i).onDragOver}
 *     onDrop={drag.handlers(i).onDrop}
 *     onDragEnd={drag.handlers(i).onDragEnd}
 *     className={cn(drag.dragOver === i && 'border-t-2 border-brand-500')}
 *   >...</li>
 */
export function useDragSort(
  _count: number,
  onReorder: (from: number, to: number) => void | Promise<void>
) {
  const fromRef = useRef<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const handlers = useCallback(
    (index: number) => ({
      onDragStart: (e: DragEvent) => {
        fromRef.current = index;
        setDragging(index);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(index)); } catch {}
      },
      onDragOver: (e: DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOver !== index) setDragOver(index);
      },
      onDragLeave: () => {
        if (dragOver === index) setDragOver(null);
      },
      onDrop: (e: DragEvent) => {
        e.preventDefault();
        const from = fromRef.current;
        const to = index;
        fromRef.current = null;
        setDragOver(null);
        setDragging(null);
        if (from === null || from === to) return;
        onReorder(from, to);
      },
      onDragEnd: () => {
        fromRef.current = null;
        setDragOver(null);
        setDragging(null);
      },
    }),
    [dragOver, onReorder]
  );

  return { handlers, dragging, dragOver };
}
